import NotificationBar from '../../ui-components/NotificationBar/NotificationBar';
import {
    ARTIKEL_LOAD_ERROR,
    KONTRAKT_PRICE_VALIDATION_ERROR,
    NEUER_ARTIKEL_BEZEICHNUNG_VALIDATION_ERROR,
    WERK_LOAD_ERROR,
    WERK_VALIDATION_ERROR,
    ZULAGE_VALIDATION_ERROR,
} from '../../constants';
import { useFormikContext } from 'formik';
import { errorForBestellpositionsField, errorForFieldExist, MAX_WEIGHT_ERROR } from '../../validation/bestellung/validation';
import { ArtikelNutztierfutterFormular, ArtikelTableValuesGrouped, getFlatBestellpositionen } from '../../helper/artikel-helper';
import { useDispatch, useSelector } from 'react-redux';
import { WerkSelectors } from '../../../store/Abholwerk.store';
import { selectArtikelLoseWareLoadFailed } from '../../../store/ArtikelLoseWare.store';
import {
    ArtikelTableValues,
    BestellpositionArtikelLoseWareView,
    BestellpositionArtikelSackwareView,
    BestellpositionView,
} from '../../types';
import { BestellungSelectors } from '../../../store/Bestellung.store';
import { ArtikelSackwarenSelectors } from '../../../store/ArtikelSackware.store';
import NonFormikCheckbox from '../../ui-components/CheckBox/NonFormikCheckbox';
import { ArtikelTyp, Severity } from '../../types/enums';
import { selectSiloValidation, setSilosConfirmed } from '../../../store/SiloValidation.store';
import { useEffect, useMemo, useState } from 'react';
import { postData } from '../../fetchApi';

type NotificationProps = {
    triedToSubmit: boolean;
    reloadArtikelCallback: () => void;
};

const MengeFormError = (): JSX.Element => {
    // TODO: Extract touched state from formik
    const { errors } = useFormikContext<ArtikelTableValues>();
    const [hasError, message] = errorForBestellpositionsField(errors, 'menge');
    return <NotificationBar message={message ?? ''} isVisible={hasError} dataCy={'menge-error-bar'} />;
};
export const WunschterminDatumFormError = (): JSX.Element => {
    // TODO: Extract touched state from formik
    const { errors } = useFormikContext<ArtikelTableValues>();
    const [hasError, message] = errorForBestellpositionsField(errors, 'debitorDatum');
    return <NotificationBar message={message ?? ''} isVisible={hasError} dataCy={'debitorDatum-error-bar'} />;
};

const SiloValidationError = (): JSX.Element => {
    const { silosConfirmed } = useSelector(selectSiloValidation);
    const { errors } = useFormikContext<ArtikelTableValues>();
    const [hasError, message] = errorForBestellpositionsField(errors, 'silo');
    const dispatch = useDispatch();
    const siloSeverity = silosConfirmed ? Severity.CONFIRMRED : Severity.ERROR;

    const handleCheckboxChange = (confirmed: boolean) => {
        dispatch(setSilosConfirmed(confirmed));
    };

    return (
        <NotificationBar isVisible={hasError} message={''} dataCy="silo-validation-message-bar" severity={siloSeverity}>
            <NonFormikCheckbox id="siloCheck" name="siloCheck" label={message} onFieldChange={handleCheckboxChange} />
        </NotificationBar>
    );
};

export const WunschterminUhrzeitFormError = (): JSX.Element => {
    // TODO: Extract touched state from formik
    const { errors } = useFormikContext<ArtikelTableValues>();
    const [hasError, message] = errorForBestellpositionsField(errors, 'debitorUhrzeit');
    return <NotificationBar message={message ?? ''} isVisible={hasError} dataCy={'debitorUhrzeit-error-bar'} />;
};

export const WunschterminDebitorTopLevelFormError = (): JSX.Element => {
    const { errors } = useFormikContext<ArtikelTableValues & { datumDebitor?: string; uhrzeitDebitor?: string }>();
    const datumError = (errors as { datumDebitor?: string })?.datumDebitor;
    const uhrzeitError = (errors as { uhrzeitDebitor?: string })?.uhrzeitDebitor;
    const message = datumError || uhrzeitError;
    return <NotificationBar message={message ?? ''} isVisible={Boolean(message)} dataCy={'datumDebitor-top-level-error-bar'} />;
};

const MengePalFormError = (): JSX.Element => {
    const { errors } = useFormikContext<ArtikelTableValues>();
    const [hasError, message] = errorForBestellpositionsField(errors, 'mengePal');
    return <NotificationBar message={message ?? ''} isVisible={hasError} dataCy={'menge-pal-error-bar'} />;
};

export const BestellpositionenNotifications = ({ reloadArtikelCallback, triedToSubmit }: NotificationProps): JSX.Element => {
    const { errors } = useFormikContext<ArtikelNutztierfutterFormular>();
    const werkeLoadFailed = useSelector(WerkSelectors.selectWerkeLoadFailed);
    const sackwareLoadFailed = useSelector(ArtikelSackwarenSelectors.loadFailed);
    const loseWareLoadFailed = useSelector(selectArtikelLoseWareLoadFailed);
    const artikelLoadFailed = sackwareLoadFailed || loseWareLoadFailed;

    const allErrors = {
        bestellpositionErrorMessage:
            typeof errors.bestellpositionenView === 'string' && errors.bestellpositionenView !== MAX_WEIGHT_ERROR
                ? errors.bestellpositionenView
                : '',
    };

    return (
        <>
            <NotificationBar
                message={ARTIKEL_LOAD_ERROR}
                actionText="Neu laden"
                actionCallback={reloadArtikelCallback}
                isVisible={artikelLoadFailed}
            />
            <NotificationBar
                dataCy={'bestellpositionen-error-message-bar'}
                message={allErrors.bestellpositionErrorMessage}
                isVisible={allErrors.bestellpositionErrorMessage !== ''}
            />
            <NotificationBar
                dataCy={'bestellpositionen-error-message-bar'}
                message={WERK_VALIDATION_ERROR}
                isVisible={errorForFieldExist(errors, 'werkId') && triedToSubmit}
                testId="werke-error-notification"
            />
            <NotificationBar
                dataCy={'zulage-error-message-bar'}
                message={ZULAGE_VALIDATION_ERROR}
                isVisible={errorForFieldExist(errors, 'zulageText') && triedToSubmit}
            />
            <NotificationBar
                dataCy={'bestellpositionen-error-message-bar'}
                message={NEUER_ARTIKEL_BEZEICHNUNG_VALIDATION_ERROR}
                isVisible={errorForFieldExist(errors, 'bezeichnung') && triedToSubmit}
            />
            <MengePalFormError />
            <MengeFormError />
            <SiloValidationError />
            <NotificationBar
                message={KONTRAKT_PRICE_VALIDATION_ERROR}
                isVisible={errorForFieldExist(errors, 'kontrakt') && triedToSubmit}
            />
            <NotificationBar message={WERK_LOAD_ERROR} isVisible={werkeLoadFailed} />
        </>
    );
};

type MindestbestellmengeHinweisProps = {
    artikelTypFilter?: ArtikelTyp;
};

export function MindestbestellmengeHinweis({ artikelTypFilter }: MindestbestellmengeHinweisProps = {}): JSX.Element {
    // Get values from Formik context - try both possible types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formikValues = useFormikContext<any>().values;
    const selectedWerkId = useSelector(BestellungSelectors.selectedWerkId);

    // Check if it's grouped format (ArtikelHeimtierfutter) or flat format (Bestellansicht/Auftragsansicht)
    const bestellpositionenView: BestellpositionView[] = useMemo(() => {
        if (formikValues.groupedBestellpositionen) {
            // ArtikelTableValuesGrouped format (ArtikelHeimtierfutter)
            return getFlatBestellpositionen(formikValues as ArtikelTableValuesGrouped);
        } else if (formikValues.bestellpositionenView) {
            // ArtikelTableValues format (Bestellansicht/Auftragsansicht)
            return (formikValues.bestellpositionenView as BestellpositionView[]) || [];
        } else {
            return [];
        }
    }, [formikValues]);

    const defaultWerkId: string | undefined = selectedWerkId;
    const [isValid, setIsValid] = useState<boolean>(true);
    const [violations, setViolations] = useState<Array<{ message: string }>>([]);

    type SimpleValidationPosition = {
        artikelNummer: string;
        werkId: string;
        menge?: number;
        mengeVe?: number;
        mengePal?: number;
    };
    type SimpleValidationPayload = { bestellpositionen: SimpleValidationPosition[] } | null;

    // Map Formik view values to minimal DTO expected by backend validator
    const payload: SimpleValidationPayload = useMemo(() => {
        const bestellpositionen: SimpleValidationPosition[] = (bestellpositionenView ?? []).reduce(
            (acc: SimpleValidationPosition[], bp) => {
                // Optional nach Artikel-Typ filtern (z. B. nur Sackware oder nur Loseware)
                if (artikelTypFilter && bp.artikelTyp !== artikelTypFilter) {
                    return acc;
                }

                const artikelNummer = bp.nummer ?? '';
                const positionWerkId = bp.werkId ?? defaultWerkId ?? '';

                if (!positionWerkId || positionWerkId.trim() === '') {
                    return acc;
                }

                if (bp.artikelTyp === ArtikelTyp.LoseWare) {
                    const raw = (bp as BestellpositionArtikelLoseWareView).menge ?? '';
                    const menge = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.')) || 0;

                    // Lose Ware nur berücksichtigen, wenn eine Menge > 0 eingegeben wurde
                    if (menge > 0) {
                        acc.push({ artikelNummer, werkId: positionWerkId, menge });
                    }
                    return acc;
                }

                const rawVe = (bp as BestellpositionArtikelSackwareView).mengeVe ?? '';
                const rawPal = (bp as BestellpositionArtikelSackwareView).mengePal ?? '';
                const mengeVe = typeof rawVe === 'number' ? rawVe : parseInt(String(rawVe)) || 0;
                const mengePal = typeof rawPal === 'number' ? rawPal : parseInt(String(rawPal)) || 0;

                // Nur Felder senden, die > 0 sind (laut Backend-Dokumentation)
                const sackwarePosition: { artikelNummer: string; werkId: string; mengeVe?: number; mengePal?: number } = {
                    artikelNummer,
                    werkId: positionWerkId,
                };
                if (mengeVe > 0) {
                    sackwarePosition.mengeVe = mengeVe;
                }
                if (mengePal > 0) {
                    sackwarePosition.mengePal = mengePal;
                }

                // Sackware-Position nur senden, wenn mindestens eine Menge > 0 gesetzt ist
                if (sackwarePosition.mengeVe || sackwarePosition.mengePal) {
                    acc.push(sackwarePosition);
                }
                return acc;
            },
            []
        );

        // Nur senden, wenn mindestens eine Position mit gesetzter Menge vorhanden ist
        return bestellpositionen.length > 0 ? { bestellpositionen } : null;
    }, [artikelTypFilter, bestellpositionenView, defaultWerkId]);

    // No debounce - validate immediately for fastest response
    const debouncedPayload = payload;

    useEffect(() => {
        let isCancelled = false;
        async function runValidation(): Promise<void> {
            if (!debouncedPayload) {
                // Nothing to validate, hide hints
                setIsValid(true);
                setViolations([]);
                return;
            }
            try {
                const resp = (await postData('bestellungen/validate', debouncedPayload)) as
                    | boolean
                    | {
                          valid?: boolean;
                          mindestbestellmenge?: number;
                          mindestbestellmengeEinheit?: string;
                          violations?: Array<Record<string, unknown>>;
                      };
                if (!isCancelled) {
                    let isOk = false;
                    let minValue: number | undefined = undefined;
                    if (typeof resp === 'object' && resp && Object.prototype.hasOwnProperty.call(resp, 'valid')) {
                        isOk = Boolean((resp as any).valid);
                        if (typeof (resp as any).mindestbestellmenge === 'number') {
                            minValue = (resp as any).mindestbestellmenge as number;
                        }
                    } else {
                        isOk = Boolean(resp as boolean);
                    }
                    setIsValid(isOk);
                    if (!isOk) {
                        // Hilfsfunktion zur Konvertierung der Backend-Einheit (TNE, KGM) in lesbare Einheit
                        const getEinheitFromCode = (einheitCode: string | null | undefined): string => {
                            if (!einheitCode) {
                                return 'Tonnen'; // Fallback
                            }
                            switch (einheitCode.toUpperCase()) {
                                case 'TNE':
                                case 'T':
                                case 'TONNEN':
                                    return 'Tonnen';
                                case 'KGM':
                                case 'KG':
                                case 'KILOGRAMM':
                                    return 'kg';
                                default:
                                    return 'Tonnen'; // Fallback
                            }
                        };

                        const toKilogramm = (wert: number, einheitCode: string | null | undefined): number | undefined => {
                            if (!Number.isFinite(wert)) {
                                return undefined;
                            }
                            if (!einheitCode) {
                                return wert;
                            }
                            const upper = einheitCode.toUpperCase();
                            if (upper === 'TNE' || upper === 'T' || upper === 'TONNEN') {
                                return wert * 1000;
                            }
                            if (upper === 'KGM' || upper === 'KG' || upper === 'KILOGRAMM') {
                                return wert;
                            }
                            return wert;
                        };

                        // Einheit aus Response extrahieren (Backend konvertiert bereits alles)
                        const responseEinheit =
                            typeof resp === 'object' && resp && Object.prototype.hasOwnProperty.call(resp, 'mindestbestellmengeEinheit')
                                ? (resp as any).mindestbestellmengeEinheit
                                : null;

                        const formatValue = (v: number) =>
                            Number.isInteger(v) ? String(v) : v.toLocaleString('de-DE', { maximumFractionDigits: 1 });

                        const formatMindestbestellmengeMitOptionalerVe = (
                            mNum: number,
                            einheitCode: string | null | undefined,
                            violation: any
                        ): string => {
                            const einheitText = getEinheitFromCode(einheitCode);

                            if (artikelTypFilter === ArtikelTyp.Sackware && bestellpositionenView.length > 0) {
                                const artikelNummerFromViolation: string | undefined =
                                    violation?.artikelNummer ??
                                    violation?.artikelnummer ??
                                    violation?.nummer ??
                                    violation?.artikelId ??
                                    undefined;

                                const passendePosition = bestellpositionenView.find(
                                    (bp) =>
                                        bp.artikelTyp === ArtikelTyp.Sackware &&
                                        artikelNummerFromViolation &&
                                        bp.nummer === artikelNummerFromViolation
                                ) as BestellpositionArtikelSackwareView | undefined;

                                const faktorVe = passendePosition?.faktorBasiseinheitVe;

                                const minInKg = toKilogramm(mNum, einheitCode);

                                if (passendePosition && typeof faktorVe === 'number' && faktorVe > 0 && typeof minInKg === 'number') {
                                    const veWert = minInKg / faktorVe;
                                    if (veWert > 0) {
                                        const veText = formatValue(veWert);
                                        const kgText = formatValue(minInKg);
                                        return `${veText} VE (${kgText} kg)`;
                                    }
                                }
                            }

                            return `${formatValue(mNum)} ${einheitText}`;
                        };

                        const genericMsg =
                            'Die Mindestbestellmenge wurde für mindestens einen Artikel nicht erreicht. Die Bestellung ist trotzdem möglich.';

                        // Priorität: 1. Violations Array, 2. Top-Level mindestbestellmenge, 3. Generic Message
                        if (Array.isArray((resp as any)?.violations) && (resp as any).violations.length > 0) {
                            // Für jede Violation eine separate Nachricht erstellen
                            const violationMessages = (resp as any).violations.map((v: any) => {
                                const name = v?.bezeichnung ?? v?.artikelBezeichnung;
                                const mNum: number | undefined = v?.mindestBestellmenge ?? v?.minMenge ?? v?.min ?? minValue;
                                // Einheit aus violation Objekt verwenden, sonst aus Top-Level
                                const violationEinheitFromObj = v?.mindestbestellmengeEinheit ?? responseEinheit;

                                const msg =
                                    mNum != null && name
                                        ? `Die Mindestbestellmenge von ${formatMindestbestellmengeMitOptionalerVe(
                                              mNum,
                                              violationEinheitFromObj,
                                              v
                                          )} wurde für den Artikel "${name}" nicht erreicht. ` +
                                          'Bitte stellen Sie sicher, dass die Mindestbestellmenge eingehalten wird.'
                                        : genericMsg;
                                return { message: msg };
                            });
                            setViolations(violationMessages);
                        } else if (typeof (resp as any)?.mindestbestellmenge === 'number' || typeof minValue === 'number') {
                            const m = (resp as any)?.mindestbestellmenge ?? minValue ?? 0;
                            const formatted = formatMindestbestellmengeMitOptionalerVe(m, responseEinheit, resp);
                            const message = `Die Mindestbestellmenge von ${formatted} wurde nicht erreicht.`;
                            setViolations([{ message }]);
                        } else {
                            setViolations([{ message: genericMsg }]);
                        }
                    } else {
                        setViolations([]);
                    }
                }
            } catch (e: unknown) {
                // On request error, do not block; show generic message only when backend explicitly reported a min value
                if (!isCancelled) {
                    setIsValid(true);
                    setViolations([]);
                }
            }
        }
        runValidation();
        return () => {
            isCancelled = true;
        };
    }, [debouncedPayload]);

    // Rendere für jede Violation eine separate NotificationBar
    if (!isValid && violations.length > 0) {
        return (
            <>
                {violations.map((violation, index) => (
                    <NotificationBar
                        key={`mindestbestellmenge-hinweis-${index}`}
                        dataCy={`mindestbestellmenge-hinweis-${index}`}
                        message={violation.message}
                        isVisible={true}
                        severity={Severity.WARNING}
                    />
                ))}
            </>
        );
    }

    return <></>;
}
